import Phaser from 'phaser';
import { Cell } from '../objects/Cell';
import { NutrientSystem } from '../systems/NutrientSystem';
import { EnvironmentSystem } from '../systems/EnvironmentSystem';
import { SharedResources } from '../utils/SharedResources';

// Scene 2: Cytoskeleton & First Pseudopod Movements
export class CytoskeletonScene extends Phaser.Scene {
  private cell!: Cell;
  private nutrientSystem!: NutrientSystem;
  private environmentSystem!: EnvironmentSystem;
  private backButton!: Phaser.GameObjects.Text;
  private gradientBackground!: Phaser.GameObjects.Sprite;
  
  // UI elements
  private uiContainer!: Phaser.GameObjects.Container;
  private energyText!: Phaser.GameObjects.Text;
  private atpText!: Phaser.GameObjects.Text;
  private cytoskeletonText!: Phaser.GameObjects.Text;
  
  // Evolution progress tracking
  private membraneStability: number = 0;
  private cytoskeletonDensity: number = 0;
  private atpEfficiency: number = 0;
  private absorbedMitochondria: number = 0;
  
  constructor() {
    super('CytoskeletonScene');
  }

  init(data: any): void {
    console.log('CytoskeletonScene init method called');
    
    // Initialize with data from previous scene if available
    if (data) {
      // Carry over data from ProtocellScene
      this.membraneStability = data.membraneStability || 0;
      
      // Initialize stage-specific properties
      this.cytoskeletonDensity = data.cytoskeletonDensity || 10; // Start with basic cytoskeleton
      this.atpEfficiency = data.atpEfficiency || 20; // Low initial efficiency
      this.absorbedMitochondria = data.absorbedMitochondria || 0;
    }
    
    // Check for and remove any leftover gradient textures
    if (this.textures.exists('gradientTexture')) {
      console.log('Found leftover gradient texture in CytoskeletonScene init, removing it');
      this.textures.remove('gradientTexture');
    }
  }

  create(): void {
    // Initialize shared resources
    const sharedResources = SharedResources.getInstance();
    if (!sharedResources.hasGradientTexture()) {
      sharedResources.initialize(this);
    }
    
    // Set background color
    this.cameras.main.setBackgroundColor('#000033');
    
    // Initialize systems
    this.nutrientSystem = new NutrientSystem(this);
    this.environmentSystem = new EnvironmentSystem(this);
    
    // Create main cell in the center
    this.cell = new Cell(this, 0, 0);
    
    // Configure camera
    this.cameras.main.setZoom(1);
    
    // Create UI
    this.createUI();
    
    // Create gradient background using shared resources
    this.gradientBackground = sharedResources.createGradientSprite(this, 0, 0);
    console.log('Created gradient background using shared resources in CytoskeletonScene');
    
    // Add camera follow
    this.cameras.main.startFollow(this.cell, true, 0.1, 0.1);
    
    // Add handlers for input
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.leftButtonDown()) return;
      
      // Check if pointer is over UI
      const isOverUI = this.uiContainer && this.uiContainer.getBounds() ? 
                       this.uiContainer.getBounds().contains(pointer.x, pointer.y) : 
                       false;
      
      // Don't move if pointer is over UI
      if (!isOverUI) {
        // Convert screen position to world position
        const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
        (this.cell as any).targetPosition = new Phaser.Math.Vector2(worldPoint.x, worldPoint.y);
      }
    });
    
    // Check if we should show evolution button
    this.checkEvolutionProgress();
  }

  update(time: number, delta: number): void {
    // Update systems
    this.nutrientSystem.update(delta);
    this.environmentSystem.update(delta);
    
    // Update cell
    this.cell.update(delta);
    
    // Update UI text
    this.updateUI();
    
    // Check if evolution criteria are met
    this.checkEvolutionProgress();
  }

  private createUI(): void {
    // Create a container for UI elements
    this.uiContainer = this.add.container(0, 0);
    
    // Add energy, ATP, and cytoskeleton indicators
    this.energyText = this.add.text(20, 20, 'Energy: 100%', {
      fontSize: '18px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0, 0);
    this.uiContainer.add(this.energyText);
    
    this.atpText = this.add.text(20, 50, 'ATP Efficiency: 20%', {
      fontSize: '18px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0, 0);
    this.uiContainer.add(this.atpText);
    
    this.cytoskeletonText = this.add.text(20, 80, 'Cytoskeleton: 10%', {
      fontSize: '18px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0, 0);
    this.uiContainer.add(this.cytoskeletonText);
    
    // Add back button
    this.backButton = this.add.text(this.scale.width - 20, 20, 'Menu', {
      fontSize: '18px',
      color: '#ffffff',
      backgroundColor: '#1a6dd7',
      padding: { x: 10, y: 5 },
      stroke: '#000000',
      strokeThickness: 1
    })
    .setOrigin(1, 0)
    .setInteractive({ useHandCursor: true })
    .on('pointerdown', () => {
      console.log('Back button clicked, returning to MainMenuScene');
      this.scene.stop('CytoskeletonScene');
      this.scene.start('MainMenuScene');
    });
    
    this.uiContainer.add(this.backButton);
    
    // Set depth to ensure UI is always on top
    this.uiContainer.setDepth(1000);
  }

  private updateUI(): void {
    // Update UI text with current values
    if (this.cell) {
      this.energyText.setText(`Energy: ${this.cell.getEnergy()}%`);
      this.atpText.setText(`ATP Efficiency: ${Math.floor(this.atpEfficiency)}%`);
      this.cytoskeletonText.setText(`Cytoskeleton: ${Math.floor(this.cytoskeletonDensity)}%`);
    }
  }

  private checkEvolutionProgress(): void {
    // Check if we've reached the threshold to evolve to the next stage
    if (this.atpEfficiency >= 80 && this.cytoskeletonDensity >= 75 && !this.hasEvolutionButton()) {
      this.createEvolutionButton();
    }
  }

  private hasEvolutionButton(): boolean {
    // Check if the evolution button already exists
    return this.children.getByName('evolutionButton') !== null;
  }

  private createEvolutionButton(): void {
    // Create a button to progress to the next evolutionary stage
    const evolutionButton = this.add.text(this.scale.width / 2, this.scale.height - 50, 'Evolve to Complex Cell Stage', {
      fontSize: '24px',
      color: '#ffffff',
      backgroundColor: '#22aa22',
      padding: { x: 20, y: 10 },
      stroke: '#000000',
      strokeThickness: 1
    })
    .setOrigin(0.5)
    .setName('evolutionButton')
    .setInteractive({ useHandCursor: true })
    .on('pointerdown', () => {
      console.log('Evolution button clicked, progressing to GameScene (Complex Cell)');
      
      // Perform cleanup before transition
      this.performCleanup();
      
      // Prepare data to pass to the next scene
      const evolutionData = {
        membraneStability: this.membraneStability,
        cytoskeletonDensity: this.cytoskeletonDensity,
        atpEfficiency: this.atpEfficiency,
        absorbedMitochondria: this.absorbedMitochondria,
        cellEnergy: this.cell.getEnergy(),
        cellHealth: this.cell.getHealth()
      };
      
      // Transition to the next scene (GameScene is the complex cell stage)
      this.scene.stop('CytoskeletonScene');
      this.scene.start('GameScene', evolutionData);
    });
    
    if (this.uiContainer) {
      this.uiContainer.add(evolutionButton);
    }
  }

  // Add a helper method for cleanup
  private performCleanup(): void {
    console.log('CytoskeletonScene performing cleanup');
    
    // Tell SharedResources we're done with the gradient texture
    if (this.gradientBackground) {
      this.gradientBackground.destroy();
    }
    SharedResources.getInstance().releaseResource('gradientTexture');
    
    console.log('CytoskeletonScene cleanup complete');
  }

  // Helper method for cleanup
  shutdown(): void {
    console.log('CytoskeletonScene shutdown method called');
    
    // Perform cleanup
    this.performCleanup();
    
    console.log('CytoskeletonScene shutdown complete');
  }
} 